import classnames from 'classnames';
import PropTypes from 'prop-types';
import * as React from 'react';

import styles from './styles.module.scss';
import type { InferPropTypes } from '../typings/index.ts';
import addVariants from '../utils/addVariants/index.ts';
import useSpacing from '../utils/useSpacing/index.ts';

export const VARIANTS = ['default'] as const;
export type RowVariant = (typeof VARIANTS)[number];
export const ALIGNMENTS = ['start', 'end', 'center', 'baseline', 'stretch'] as const;
export const JUSTIFICATIONS = ['start', 'end', 'center', 'space-around', 'space-between'] as const;

const propTypes = {
  /**
   * Any children to be rendered
   */
  children: PropTypes.node.isRequired,
  /**
   * Additional classnames to apply
   */
  className: PropTypes.string,
  /**
   * Decides the variant
   */
  variant: PropTypes.oneOfType([
    PropTypes.oneOf<RowVariant>(VARIANTS).isRequired,
    PropTypes.bool.isRequired,
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf<RowVariant>(VARIANTS).isRequired,
        PropTypes.bool.isRequired
      ]).isRequired
    )
  ]),
  /**
   * Decides the default vertical aligning of the `Col`s
   * Maps directly to CSS `align-items`
   */
  align: PropTypes.oneOf<(typeof ALIGNMENTS)[number]>(ALIGNMENTS),
  /**
   * Decides default horizontal aligning of the `Col`s
   * Maps directly to CSS `justify-content`
   */
  justify: PropTypes.oneOf<(typeof JUSTIFICATIONS)[number]>(JUSTIFICATIONS),
  /**
   * Allow customization of the margin around the `Row`. See `Spacer` Component
   */
  spacing: PropTypes.shape({
    top: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    bottom: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    left: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    right: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    x: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    y: PropTypes.oneOfType([PropTypes.number, PropTypes.bool])
  }),
  /**
   * Custom styles to apply, use sparingly
   */
  style: PropTypes.shape({}),
  /**
   * Any additional props to attach to the `Row`'s underlying `div`
   */
  containerProps: PropTypes.shape({})
};

const defaultProps = {
  className: '',
  variant: 'default',
  align: null,
  justify: null,
  spacing: {},
  style: {},
  containerProps: {}
};

export interface RowOverrideProps {
  containerProps: React.HTMLAttributes<HTMLDivElement>;
  style: React.CSSProperties;
}

export type RowProps = InferPropTypes<typeof propTypes, typeof defaultProps, RowOverrideProps>;

/**
 * A `Row` is useful in conjunction with `Col` for creating horizontal or vertical layouts
 * It is essentially a ~flex container~. And cannot be used in EHR apps
 */
const Row: React.FC<RowProps> = ({
  variant,
  className,
  children,
  align,
  justify,
  spacing,
  style,
  containerProps
}: RowProps) => {
  const spaceStyles = useSpacing(spacing, { style });
  return (
    <div
      className={classnames(
        addVariants('aglmd-row', styles, variant, `align-${align}`, `justify-${justify}`),
        className
      )}
      style={spaceStyles}
      {...containerProps}
    >
      {children}
    </div>
  );
};

Row.propTypes = propTypes;
Row.defaultProps = defaultProps;

export default Row;
